import React, { ChangeEvent, Dispatch, SetStateAction } from "react";
import axios from "axios";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import DeleteIcon from "@material-ui/icons/Delete";
import { useTextField } from "./Form";
import Swal from "sweetalert2";
import { handleDownloadFileError } from "../utils/errors";

type FileExtension = {
  // esto es lo que ve el usuario
  extension: string; // ej. ".pdf"

  // esto es lo que revisa la validación de formato
  // así nos evitamos problemas de si es por ejemplo .jpg o .jpeg
  mimeType: string; // ej. "application/pdf"
};

export const allowedExtensions: FileExtension[] = [
  { extension: ".pdf", mimeType: "application/pdf" },
  { extension: ".doc", mimeType: "application/msword" },
  { extension: ".jpg", mimeType: "image/jpeg" },
  { extension: ".png", mimeType: "image/png" },
  {
    extension: ".docx",
    mimeType:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  },
];

export interface IAttachment {
  readonly id?: number; // id from the backend. If present, you can't change the file anymore.
  readonly nombre: string; // Label provided by the user. This is not the name of the file in the filesystem.
  readonly archivo: File | null; // will contain a file only if the attachment hasn't been uploaded to the backend yet.
  readonly reactKey: string; // this should be unique across all attachments to avoid bugs
  readonly deleted?: boolean;
}

export class Attachment implements IAttachment {
  readonly id?: number;
  readonly nombre: string;
  readonly archivo: File | null;
  readonly reactKey: string;
  readonly deleted?: boolean;

  constructor(props: IAttachment) {
    this.id = props.id;
    this.nombre = props.nombre;
    this.archivo = props.archivo;
    this.reactKey = props.reactKey;
    this.deleted = props.deleted;
  }

  static empty(): Attachment {
    return new Attachment({
      id: undefined, // as it hasn't been uploaded to the backend yet
      nombre: "",
      archivo: null,
      reactKey: Math.random().toString(), // unique across all attachments
      deleted: false,
    });
  }

  // Implements all the setters in a single method.
  // Note: it returns a new Attachment instance, keeping the original intact
  // example: renamedAttachment = attachment.set({ nombre: "nuevo nombre" });
  set(fields: Partial<IAttachment>): Attachment {
    return this.new({ ...this, ...fields });
  }

  new(props: IAttachment): Attachment {
    return new Attachment(props);
  }

  // used by form validation
  get isValid(): boolean {
    return this.validate() === "OK";
  }

  // used by form validation
  validate(): string {
    if (this.id != null || this.id !=-1) {
      return "OK"; // as we assume the attachments from the backend are always valid
    }

    if (this.archivo == null) {
      return "Debe elegir un archivo";
    }

    const attachmentMimeType = this.archivo.type;
    if (
      !allowedExtensions.some(
        (extension) => attachmentMimeType === extension.mimeType
      )
    ) {
      return "El archivo no pertenece a ninguno de los formatos permitidos";
    }

    return "OK";
  }
}

export type FileFieldProps = {
  // this is a controlled component
  attachment: Attachment;
  setAttachment: Dispatch<SetStateAction<Attachment>>;
};

export const FileField: React.FC<FileFieldProps> = (props) => {
  const { attachment, setAttachment } = props;

  const nombreField = useTextField({
    fullWidth: true,
    label: "Nombre archivo",
    name: attachment.reactKey,
    defaultValue: attachment.nombre,
    requiredFieldMessage: "Indique el nombre del documento",

    onChange(event: ChangeEvent<HTMLInputElement>): void {
      const nombre = event.target.value;
      setAttachment((x) => x.set({ nombre }));
    },

    validate(_value: string): string {
      // this field will also show the validation errors of the attachment, as it doesn't have it's own field
      return attachment.validate();
    },
  });

  function onDelete() {
    setAttachment((x) => x.set({ deleted: true }));
  }

  if (attachment.deleted) {
    return null;
  }

  return (
    <Grid container>
      <Grid container item spacing={2} xs={11}>
        <Grid item xs={6}>
          {nombreField.render()}
        </Grid>
        <Grid item xs={6} className="d-flex">
          <AttachOrDownload
            attachment={attachment}
            setAttachment={setAttachment}
            enableAttach={attachment.id == null}
            enableDownload={attachment.id != null}
            accept={allowedExtensions.map((e) => e.mimeType).join(",")}
            route="downloadFile"
            onBlur={nombreField.onBlur}
          />
        </Grid>
      </Grid>
      <Grid container item xs={true} justify="flex-end">
        <IconButton
          className="mt-3 align-self-center justify-self-end"
          style={{ height: "max-content" }}
          color="secondary"
          onClick={onDelete}
        >
          <DeleteIcon />
        </IconButton>
      </Grid>
    </Grid>
  );
};

type AttachOrDownloadProps = {
  attachment: Attachment;
  setAttachment: Dispatch<SetStateAction<Attachment>>;
  enableAttach: boolean;
  enableDownload: boolean;
  accept: string;
  onBlur?(): void;
  route: string;
};

export const AttachOrDownload: React.FC<AttachOrDownloadProps> = (props) => {
  function onChangeSelectedFiles(files: FileList) {
    if (files.length === 0) {
      // no files selected --> do nothing
    } else if (files.length === 1) {
      const archivo = files.item(0)!;
      props.setAttachment((x) => x.set({ archivo }));
    } else {
      alert("Seleccione solo un archivo a la vez");
    }

    props.onBlur?.();
  }

  function downloadFile() {
    if (props.attachment.id !== -1) {
      axios({
        url: `${(window as any).ENV.API_URL}/${props.route}/${props.attachment.id
          }/`,
        method: "GET",
        responseType: "blob", // important
      })
        .then((response) => {
          const file = new Blob([response.data], { type: response.data.type });
          const fileURL = URL.createObjectURL(file);
          const link = document.createElement("a");
          link.href = fileURL;
          link.download = props.attachment.nombre.toString().replace(/[\/.,]/g,'_').substring(0,20).replace(/\s+$/, ''); // the label provided by the user will be used as the default file name
          link.click();
        })
        .catch((error) => handleDownloadFileError(error));
    } else {
      return Swal.fire({
        icon: "warning",
        text: " El antecedente no presenta documentos adjuntos",
      });
    }
  }

  return (
    <React.Fragment>
      {props.enableAttach && (
        <input
          type="file"
          name={props.attachment.reactKey}
          id={props.attachment.reactKey}
          className="mt-3 align-self-center w-100"
          style={{ textOverflow: "ellipsis" }}
          onChange={(event) => {
            if (event.target.files == null) {
              alert(
                "event.target.files puede ser null.\n" +
                "Si ves esto, dile a nuestros desarrolladores cómo lo hiciste"
              );
            } else {
              onChangeSelectedFiles(event.target.files);
            }
          }}
          accept={props.accept}
        />
      )}

      {props.enableDownload && (
        <Button
          color="primary"
          className="mt-3 align-self-center"
          style={{ height: "max-content" }}
          onClick={downloadFile}
        >
          Ver archivo
        </Button>
      )}
    </React.Fragment>
  );
};
